// src/webgl/oceanCanvas.ts — Three.js Atmospheric Deep-Sea Canvas (NFR-RENDER-001)
import * as THREE from 'three';

export interface OceanCanvasOptions {
  particleCount?: number;
  interactiveMouse?: boolean;
}

export class OceanCanvas {
  private container: HTMLElement;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer | null = null;
  private particles: THREE.Points | null = null;
  private particlePositions: Float32Array | null = null;
  private particleVelocities: Float32Array | null = null;
  private animFrameId: number | null = null;
  private mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };
  private startTime = performance.now();
  private lightBeams: THREE.Mesh | null = null;
  private isDestroyed = false;

  constructor(container: HTMLElement, options: OceanCanvasOptions = {}) {
    this.container = container;
    const count = options.particleCount ?? 200;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a192f, 0.0035);

    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.z = 100;

    this.initRenderer();
    if (this.renderer) {
      this.initParticles(count);
      this.initLightCaustics();
      this.initEventListeners();
      this.animate();
    }
  }

  private initRenderer(): void {
    try {
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: true,
        powerPreference: 'high-performance',
      });
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      const canvas = this.renderer.domElement;
      canvas.id = 'ocean-webgl-canvas';
      canvas.style.position = 'fixed';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.zIndex = '0';
      canvas.style.pointerEvents = 'none';

      this.container.prepend(canvas);
    } catch (err) {
      console.warn('WebGL not supported or disabled; using rich CSS atmospheric fallback:', err);
      this.renderer = null;
    }
  }

  private initParticles(count: number): void {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    const cyanColor = new THREE.Color(0x00e5ff);
    const goldColor = new THREE.Color(0xe5a93c);
    const emeraldColor = new THREE.Color(0x3d5a45);

    for (let i = 0; i < count; i++) {
      // Spread across camera frustum
      positions[i * 3] = (Math.random() - 0.5) * 220;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 160;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 120;

      // Gentle upward drift & horizontal sway
      velocities[i * 3] = (Math.random() - 0.5) * 0.05;
      velocities[i * 3 + 1] = Math.random() * 0.08 + 0.02; // upward
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.05;

      // Color variation between cyan, gold, and emerald
      const rand = Math.random();
      const col = rand > 0.6 ? cyanColor : rand > 0.3 ? goldColor : emeraldColor;
      colors[i * 3] = col.r;
      colors[i * 3 + 1] = col.g;
      colors[i * 3 + 2] = col.b;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Canvas circular particle texture for soft glowing bubbles
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.3, 'rgba(0, 229, 255, 0.8)');
      gradient.addColorStop(0.7, 'rgba(26, 54, 93, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 64, 64);
    }
    const texture = new THREE.CanvasTexture(canvas);

    const material = new THREE.PointsMaterial({
      size: 4.5,
      map: texture,
      vertexColors: true,
      transparent: true,
      opacity: 0.75,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.particles = new THREE.Points(geometry, material);
    this.particlePositions = positions;
    this.particleVelocities = velocities;
    this.scene.add(this.particles);
  }

  private initLightCaustics(): void {
    // Volumetric shimmering sunbeams piercing from top-right
    const beamGeo = new THREE.PlaneGeometry(160, 220, 16, 16);
    const beamMat = new THREE.MeshBasicMaterial({
      color: 0x00e5ff,
      transparent: true,
      opacity: 0.06,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    this.lightBeams = new THREE.Mesh(beamGeo, beamMat);
    this.lightBeams.rotation.z = -Math.PI / 6;
    this.lightBeams.position.set(40, 30, -40);
    this.scene.add(this.lightBeams);
  }

  private initEventListeners(): void {
    window.addEventListener('resize', this.onResize);
    window.addEventListener('mousemove', this.onMouseMove);
  }

  private onResize = (): void => {
    if (!this.renderer) return;
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };

  private onMouseMove = (e: MouseEvent): void => {
    this.mouse.targetX = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouse.targetY = -(e.clientY / window.innerHeight) * 2 + 1;
  };

  private animate = (): void => {
    if (this.isDestroyed || !this.renderer) return;

    this.animFrameId = requestAnimationFrame(this.animate);
    const elapsedTime = (performance.now() - this.startTime) * 0.001;

    // Smooth mouse interpolation
    this.mouse.x += (this.mouse.targetX - this.mouse.x) * 0.05;
    this.mouse.y += (this.mouse.targetY - this.mouse.y) * 0.05;

    // Gently tilt camera based on mouse
    this.camera.position.x = this.mouse.x * 12;
    this.camera.position.y = this.mouse.y * 8;
    this.camera.lookAt(0, 0, 0);

    // Animate particles
    if (this.particles && this.particlePositions && this.particleVelocities) {
      const pos = this.particlePositions;
      const vel = this.particleVelocities;
      const count = pos.length / 3;

      for (let i = 0; i < count; i++) {
        const idxX = i * 3;
        const idxY = i * 3 + 1;
        const currentY = pos[idxY];
        const currentX = pos[idxX];
        const velY = vel[idxY];
        const velX = vel[idxX];

        if (currentY !== undefined && velY !== undefined && currentX !== undefined && velX !== undefined) {
          const nextY = currentY + velY;
          const nextX = currentX + Math.sin(elapsedTime * 0.8 + i) * 0.08 + velX;

          pos[idxY] = nextY > 90 ? -90 : nextY;
          pos[idxX] = nextY > 90 ? (Math.random() - 0.5) * 220 : nextX;
        }
      }
      this.particles.geometry.attributes['position']!.needsUpdate = true;
    }

    // Undulate light beams
    if (this.lightBeams) {
      this.lightBeams.rotation.z = -Math.PI / 6 + Math.sin(elapsedTime * 0.5) * 0.05;
      (this.lightBeams.material as THREE.MeshBasicMaterial).opacity =
        0.05 + Math.sin(elapsedTime * 0.8) * 0.02;
    }

    this.renderer.render(this.scene, this.camera);
  };

  destroy(): void {
    this.isDestroyed = true;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
    }
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMouseMove);

    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.domElement.remove();
      this.renderer = null;
    }
  }
}
